import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core'
import classNames from 'classnames'
import React, { DependencyList, useState } from 'react'

import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import ChartContainer, { IToggleIcon } from 'src/components/ChartContainer'

import { IOutput, exportData } from '../../export/Export'

import { useGraphContext } from './GraphWrappers'
import { exportDeckGLChartToImg, exportReactChart2ToImg } from './utils'

export interface IDashboardGraphProps extends WithStyles<typeof styles> {
  bannerMessage?: string
  customExport?: (filename: string, data?: any) => void
  defaultData?: any
  description?: string
  disableImageExport?: boolean
  // FIXME(alaurent, updated 2022-09-01 - 2023/03/06) : this component should not fetch data itself,
  // but receive it as props.
  // When ready to make the change, the "good" behaviour (receiving data as props) is already implemented
  // by ChartWrapper.tsx. At this point, API calls should be made (and debounced if required) in the parent
  // component of ChartWrapper.tsx.
  fetcher: (signal: AbortSignal) => Promise<any>
  formatExportBuckets?: (data: any) => IOutput
  formatExportFeatures?: (data: any) => IOutput
  halfContainer?: boolean
  hideBanner?: (data?: any, bannerMessage?: string) => string
  id?: string
  render: (data: any, title?: string) => React.ReactNode
  title: string
  toggles?: IToggleIcon[]
  updateOn?: DependencyList
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.background.paper,
      borderTopRightRadius: 'inherit',
      borderBottomRightRadius: 'inherit',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      overflowY: 'auto',
      overflowX: 'unset',
    },
    chartContainer: {
      flex: '1 1 100%',
    },
    halfContainer: {
      flex: '1 1 50%',
      width: '50%',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
    },
    close: {
      padding: theme.spacing(1 / 2),
    },
  })

export const DashboardGraph = withStyles(styles)((props: IDashboardGraphProps) => {
  const {
    title,
    fetcher,
    render,
    classes,
    formatExportBuckets,
    formatExportFeatures,
    customExport,
    description,
    toggles,
    bannerMessage,
    hideBanner,
    id,
    defaultData,
    disableImageExport,
  } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [data, setData] = useState<any>(defaultData ?? [])

  const { reactChart, deckGL } = useGraphContext()

  const fetchDataBaseChart = async function (signal: AbortSignal) {
    setIsLoading(true)
    fetcher(signal)
      .then((results: any) => {
        setData(results)
      })
      .catch((err: string) => {
        console.error(`ERROR while fetching data for "${title}"`, err)
        setData(defaultData ?? [])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useDeepCompareEffect(() => {
    const abortController = new AbortController()
    fetchDataBaseChart(abortController.signal)
    return () => abortController.abort()
  }, props.updateOn ?? [])

  let onExport
  if (formatExportBuckets) {
    const rows = data || []
    onExport = () => exportData(title, rows, formatExportBuckets, true)
  } else if (formatExportFeatures) {
    const geojson = data || { features: [] }
    const rows = geojson.features
    onExport = () => exportData(title, rows, formatExportFeatures, true)
  } else if (customExport) {
    onExport = () => customExport(title, data)
  }

  const exportGraphImage = async () => {
    // solution from https://stackoverflow.com/questions/62039681/react-chartjs-2-doughnut-chart-export-to-png
    // If better solution don't hesitate :)
    if (!!reactChart.chartRef?.current) {
      exportReactChart2ToImg(reactChart)
    }

    if (!!deckGL.deckRef?.current) {
      exportDeckGLChartToImg(deckGL)
    }
  }

  return (
    <div id={id} className={classNames(classes.chartContainer)}>
      <ChartContainer
        loading={isLoading}
        title={title}
        onExport={onExport}
        onImageExport={exportGraphImage}
        disableImageExport={disableImageExport}
        description={description}
        toggles={toggles}
        bannerMessage={hideBanner && hideBanner(data, bannerMessage)}
      >
        {render(data, title)}
      </ChartContainer>
    </div>
  )
})
